// combatpro/views/PlayersView.js
//
// Combat PRO - PlayersView (Stage 48C-0: Players Runtime Foundation).
//
// Stage 33 left this as the view-players shell with a placeholder players.map.
// Stage 48C-0 populates it with the Players panel, migrating viRenderPlayers
// (battletracker.html 9036-9098) from imperative innerHTML into declarative JSX:
//   - Player Management header (9074-9077).
//   - Add Player form (9078-9093): name / HP / Max HP / AC / Initiative, then a
//     "+ Add Player" button. The legacy read DOM inputs (vip-*) in viAddPlayer;
//     here the five fields are local view state and are passed to addPlayer, which
//     applies the legacy defaults and the empty-name guard.
//   - Per-player cards (9045-9070): name, HP/AC/INIT line, HP bar (getHpColor),
//     six ability boxes with modifiers (abilityModifier), conditions line, notes
//     textarea (controlled -> updatePlayerNotes), and the per-card amount input
//     with Damage / Heal / Remove. The per-card amount is local view state keyed
//     by player id (the legacy vip-amt-{id} input, read at click time).
//   - Empty-state message when there are no players (9094-9096).
//
// VISUAL RECOVERY: presentation moved from inline white/pale-blue styles to the
// scoped Combat PRO theme (combatpro/styles/combatProTheme.css). Only genuinely
// dynamic runtime values remain inline: the HP bar fill width (pct) and colour
// (getHpColor). All player state and mutation are owned by CombatContext; this
// view renders and forwards only. No DOM manipulation, no getElementById, no
// persistence. Authority 05 composes player spell controls over this record; the
// card's core HP/ability management remains unchanged. No optional chaining.
//
// G268: the Pets & Animal Companions entry opens a full modal from the Player
// section (not a page pill); admitted animal companions appear as tagged cards.

import React, { useContext, useState } from 'react';
import { getRt, recordDeathSave, resetDeathSaves, setTempHP } from '../runtime/combatRuntime';
import { CombatContext } from '../contexts/CombatContext';
import { getHpColor, abilityModifier } from '../players/playerEngine';
import PetsCompanionsModal from '../companions/PetsCompanionsModal';
import PlayerSpellsChip, { PlayerSpellAssignment } from '../integration/PlayerSpellsWorkspace';
import PlayerInventoryChip from '../integration/PlayerInventoryWorkspace';
import CompanionCombatControls from '../companions/CompanionCombatControls';

const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];

function PlayersView() {
  const { state, addPlayer, removePlayer, damagePlayer, healPlayer, updatePlayerNotes, setPlayerRt } = useContext(CombatContext);
  const players = state.players;

  const [form, setForm] = useState({ name: '', hp: '', maxHp: '', ac: '', initiative: '' });
  const [amounts, setAmounts] = useState({});
  const [petsOpen, setPetsOpen] = useState(false);

  function updateForm(field, val) {
    setForm(prev => {
      const next = { ...prev };
      next[field] = val;
      return next;
    });
  }

  function submitAdd() {
    const name = form.name.trim();
    if (!name) { return; }
    addPlayer({ name: name, hp: form.hp, maxHp: form.maxHp, ac: form.ac, initiative: form.initiative });
    setForm({ name: '', hp: '', maxHp: '', ac: '', initiative: '' });
  }

  function setAmount(id, val) {
    setAmounts(prev => {
      const next = { ...prev };
      next[id] = val;
      return next;
    });
  }

  function amountFor(id) {
    const v = amounts[id];
    return (v === undefined || v === null) ? '' : v;
  }

  function amountInt(id) {
    const n = parseInt(amounts[id], 10);
    return isNaN(n) ? 0 : n;
  }

  return (
    <div className="vi-view" id="view-players">
      <div id="vi-players-root">
        <div className="vi-players-panel">
          <div className="vi-panel-intro">
            <div className="vi-panel-intro-title">🧙 Player Management</div>
            <div className="vi-panel-intro-desc">Add and manage party members for full encounter visibility. Players are tracked alongside monsters but do not use monster systems.</div>
          </div>

          <div className="vi-pac-entry">
            <button type="button" className="vi-btn vi-btn-primary" onClick={() => setPetsOpen(true)}>Pets &amp; Animal Companions</button>
            <span className="vi-pac-entry-desc">Each character's saved animals, their growth, roles and combat readiness. Only eligible animals can join a fight.</span>
          </div>
          <PetsCompanionsModal isOpen={petsOpen} onClose={() => setPetsOpen(false)} />

          <div className="vi-form-card">
            <div className="vi-form-card-title">Add Player</div>
            <div className="vi-player-form-grid">
              <div className="vi-field-block"><label className="vi-field-label">Name</label>
                <input id="vip-name" type="text" className="vi-input" placeholder="Player name" value={form.name} onChange={(e) => updateForm('name', e.target.value)} /></div>
              <div className="vi-field-block"><label className="vi-field-label">HP</label>
                <input id="vip-hp" type="number" className="vi-input" placeholder="HP" min="0" value={form.hp} onChange={(e) => updateForm('hp', e.target.value)} /></div>
              <div className="vi-field-block"><label className="vi-field-label">Max HP</label>
                <input id="vip-maxhp" type="number" className="vi-input" placeholder="Max HP" min="0" value={form.maxHp} onChange={(e) => updateForm('maxHp', e.target.value)} /></div>
              <div className="vi-field-block"><label className="vi-field-label">AC</label>
                <input id="vip-ac" type="number" className="vi-input" placeholder="AC" min="0" value={form.ac} onChange={(e) => updateForm('ac', e.target.value)} /></div>
              <div className="vi-field-block"><label className="vi-field-label">Initiative</label>
                <input id="vip-init" type="number" className="vi-input" placeholder="Init" value={form.initiative} onChange={(e) => updateForm('initiative', e.target.value)} /></div>
            </div>
            <button onClick={submitAdd} className="vi-btn vi-btn-primary">+ Add Player</button>
          </div>

          {players.length === 0
            ? <div className="vi-empty">No players added yet.</div>
            : (
              <div className="vi-players-grid">
                {players.map((p) => {
                  const pct = p.maxHp ? Math.max(0, Math.min(100, (p.hp / p.maxHp) * 100)) : 0;
                  const col = getHpColor(p.hp, p.maxHp);
                  const prt = getRt(p);
                  return (
                    <div key={p.id} className="vi-player-card">
                      <div className="vi-player-name">{p.name}{p.companion ? <span className="vi-pac-tag">Animal companion</span> : null}</div>
                      <div className="vi-player-stat">HP: <strong>{p.hp} / {p.maxHp}</strong>{' | '}AC: <strong>{p.ac}</strong>{' | '}INIT: <strong>{p.initiative}</strong></div>
                      <div className="vi-hpbar">
                        <div className="vi-hpbar-fill" style={{ width: pct + '%', background: col }}></div>
                      </div>
                      <div className="vi-ability-grid6">
                        {ABILITIES.map((s) => {
                          const mod = abilityModifier(p[s]);
                          return (
                            <div key={s} className="vi-ability-mini">
                              <div className="vi-ability-mini-label">{s}</div>
                              <div className="vi-ability-mini-val">{p[s]}</div>
                              <div className="vi-ability-mini-mod">{(mod >= 0 ? '+' : '') + mod}</div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="vi-player-cond">Conditions: {p.conditions.length ? p.conditions.join(', ') : 'None'}</div>
                      {!p.companion ? <PlayerSpellAssignment playerId={p.id} /> : null}
                      {p.companion ? <CompanionCombatControls playerId={p.id} /> : null}
                      <textarea className="vi-player-notes" placeholder="Notes..." value={p.notes} onChange={(e) => updatePlayerNotes(p.id, e.target.value)} />
                      <div className="vi-player-actions">
                        <PlayerSpellsChip playerId={p.id} />
                        <PlayerInventoryChip playerId={p.id} />
                        <input type="number" className="vi-input vi-input-amt" id={'vip-amt-' + p.id} placeholder="Amount" min="1" value={amountFor(p.id)} onChange={(e) => setAmount(p.id, e.target.value)} />
                        <button onClick={() => damagePlayer(p.id, amountInt(p.id))} className="vi-btn vi-btn-sm vi-btn-danger">Damage</button>
                        <button onClick={() => healPlayer(p.id, amountInt(p.id))} className="vi-btn vi-btn-sm vi-btn-success">Heal</button>
                        <button onClick={() => removePlayer(p.id)} className="vi-btn vi-btn-sm vi-btn-secondary vi-ml-auto">Remove</button>
                      </div>
                      <div className="vi-player-ds">
                        <span className="vi-co-ds succ">Death saves S {prt.deathSaves.successes}/3</span>
                        <button className="vi-btn vi-btn-sm" onClick={() => setPlayerRt(p.id, function (r) { return recordDeathSave(r, 'success'); })}>+ Success</button>
                        <span className="vi-co-ds fail">F {prt.deathSaves.failures}/3</span>
                        <button className="vi-btn vi-btn-sm vi-btn-danger" onClick={() => setPlayerRt(p.id, function (r) { return recordDeathSave(r, 'failure'); })}>+ Failure</button>
                        <button className="vi-btn vi-btn-sm" onClick={() => setPlayerRt(p.id, resetDeathSaves)}>Reset</button>
                        <span className="vi-co-ds">Temp HP</span>
                        <input type="number" min="0" className="vi-input vi-input-amt" value={prt.tempHP} onChange={(e) => setPlayerRt(p.id, function (r) { return setTempHP(r, e.target.value === '' ? 0 : parseInt(e.target.value, 10)); })} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default PlayersView;
